import { useTranslation } from "react-i18next";
import { Moon, Sun } from "lucide-react";
import { useUIStore } from "@/stores/useUIStore";
import { cn } from "@/lib/utils";

/**
 * Always-visible light/dark switch.
 *
 * Writes straight to the UI store; ThemeProvider owns the DOM class,
 * localStorage and cross-tab sync, so this stays in lockstep with the
 * switch in Profile → Settings.
 *
 * The button is named for the mode it switches *to*, so the tooltip says
 * something the icon does not already say. That rules out aria-pressed: a
 * name that changes with state plus a pressed state contradict each other
 * ("Light mode, pressed"), so this is a plain action button.
 */
function ThemeToggle({ className }: { className?: string }) {
  const { t } = useTranslation();
  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);
  const isDark = theme === "dark";

  const label = isDark
    ? t("topbar.theme.switchToLight", "Switch to light mode")
    : t("topbar.theme.switchToDark", "Switch to dark mode");
  const tooltip = isDark
    ? t("topbar.theme.light", "Light mode")
    : t("topbar.theme.dark", "Dark mode");

  return (
    <button
      type="button"
      onClick={() => setTheme(isDark ? "light" : "dark")}
      aria-label={label}
      title={tooltip}
      className={cn(
        "rounded-full p-2 text-text-tertiary transition-colors hover:bg-surface-tertiary hover:text-text-secondary",
        "outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
        className,
      )}
    >
      {isDark ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
    </button>
  );
}

export { ThemeToggle };
